import { TechshowPortalContent } from "@app/types/ServerCustomContext";

export const BASE_TECHSHOW_PATH = "/tech-show";
export const BASE_BROCHURE_PATH = BASE_TECHSHOW_PATH + "/brochures";
export const BASE_BROCHURE_VIEW_PATH = BASE_TECHSHOW_PATH + "/view-brochure";

/**
 * Builds the filter path segment from a category and brand.
 * e.g. all/acer, cpu/acer, cpu
 */
export const buildFilterSegment = (
  category?: string,
  brand?: string,
): string => {
  const pathParts: string[] = [];
  if (category) {
    pathParts.push(category);
    if (brand) {
      pathParts.push(brand);
    }
  } else if (brand) {
    pathParts.push("all", brand);
  }
  return pathParts.join("/");
};

export const joinPaths = (...paths: string[]) => {
  const filteredPaths = paths.filter(Boolean);
  if (filteredPaths.length === 0) {
    return "/";
  }
  const normalizedPaths = filteredPaths.map((segment) => {
    return segment
      .replace(/^\/+/, "") // Remove all leading slashes
      .replace(/\/+$/, "") // Remove all trailing slashes
      .replace(/^([^?])/, "/$1"); // Prepend "/" unless the segment starts with "?"
  });
  const joinedPath = normalizedPaths.join("");
  return joinedPath;
};

export const removeDuplicatePivot = (
  items: { brand: string; category: string }[],
) => {
  const uniqueItemsMap = new Map<string, { brand: string; category: string }>();
  for (const item of items) {
    // Create a simple, unique key for each brand/category pair
    const key = `${item.brand}:${item.category}`;
    // Only set the value if the key does NOT already exist
    if (!uniqueItemsMap.has(key)) {
      uniqueItemsMap.set(key, item);
    }
  }

  // The map's values will be the unique items
  return Array.from(uniqueItemsMap.values());
};

export const combineArrays = (
  brands: string[],
  categories: string[],
): { brand: string; category: string }[] => {
  const combined = brands.flatMap((brand) =>
    categories.map((category) => ({
      brand,
      category,
    })),
  );
  return combined;
};

export const getBrandFilterState = (
  allBrands: TechshowPortalContent["filterBrands"] = [],
  map: TechshowPortalContent["filterMap"],
  selectedBrand: string,
  selectedCategory: string, // Depends on the selected category
) => {
  const brandOptions = allBrands.map((brand) => {
    // A brand is disabled if a category is selected AND this brand isn't valid for that category.
    const isDisabled =
      !!selectedCategory &&
      brand.value !== "" &&
      !map?.some(
        (item) =>
          item.brand === brand.value && item.category === selectedCategory,
      );

    return {
      ...brand,
      disabled: isDisabled,
    };
  });

  // If the currently selected brand is now disabled, default to the "All Brands" option.
  const brandValue = brandOptions.find(
    (brand) => brand.value === selectedBrand && !brand.disabled,
  )
    ? selectedBrand
    : "";

  return { brandOptions, brandValue };
};

export const getCategoryFilterState = (
  allCategories: TechshowPortalContent["filterCategories"] = [],
  map: TechshowPortalContent["filterMap"],
  selectedCategory: string,
  selectedBrand: string, // Depends on the selected brand
) => {
  const categoryOptions = allCategories.map((category) => {
    // A category is disabled if a brand is selected AND this category isn't valid for that brand.
    const isDisabled =
      !!selectedBrand &&
      category.value !== "" &&
      !map?.some(
        (item) =>
          item.category === category.value && item.brand === selectedBrand,
      );

    return {
      ...category,
      disabled: isDisabled,
    };
  });

  // If the currently selected category is now disabled, default to the "All Categories" option.
  const categoryValue = categoryOptions.find(
    (category) => category.value === selectedCategory && !category.disabled,
  )
    ? selectedCategory
    : "";

  return { categoryOptions, categoryValue };
};

type BuildCanonicalPathType = {
  basePath: string;
  brochureAction?: string;
  brochureViewId?: string;
  filterCategorySelected?: string;
  filterBrandSelected?: string;
};

export const buildCanonicalPath = ({
  basePath,
  brochureAction,
  brochureViewId,
  filterCategorySelected,
  filterBrandSelected,
}: BuildCanonicalPathType) => {
  const canonicalPathParts = [basePath];
  if (brochureAction === "view-brochure" && brochureViewId) {
    canonicalPathParts.push(brochureAction, brochureViewId);
  }
  if (brochureAction === "brochures") {
    canonicalPathParts.push("brochures");
    const filterSegment = buildFilterSegment(
      filterCategorySelected,
      filterBrandSelected,
    );
    if (filterSegment) {
      canonicalPathParts.push(filterSegment);
    }
  }

  return canonicalPathParts.join("/");
};
